/** Диапазонное значение отбора числовой колонки: обе границы включительно, любая может пустовать */
export type NumericFilterRange = { from?: number; to?: number };

/** Элемент перечня: конкретное значение либо промежуток с включительными границами */
export type NumericFilterItem = number | NumericFilterRange;

/** Пределы, за которыми значение колонкой не принимается */
export type NumericValueLimits = {
  /** Значения колонки — целые числа */
  isIntegerOnly?: boolean;
  min?: number;
  max?: number;
};

/** Разбор перечисленного: принятые — элементами, непринятые — как набраны */
export type NumericItemsParseResult = { items: NumericFilterItem[]; rejected: string[] };

/**
 * Промежуток набирается парой границ через тире; пробелы вокруг него необязательны. Границу можно
 * и опустить — тогда промежуток открыт с этой стороны. Опущенная нижняя граница отделяется от тире
 * пробелом (`- 10`): вплотную такая запись читается отрицательным числом.
 */
const RANGE_PATTERN = /^(?:([+-]?\d+(?:\.\d+)?)\s*[-–—]\s*([+-]?\d+(?:\.\d+)?)?|[-–—]\s+([+-]?\d+(?:\.\d+)?))$/;

/** Элементы в строке разделяются запятой; вставленный столбец значений приходит переводами строки */
const ITEMS_SEPARATOR = /[,\n]/;

/** Значение отбора — перечень набранных элементов, а не границы */
export const isNumericValuesFilter = (value: unknown): value is unknown[] => Array.isArray(value);

/** Значение отбора — границы; всё прочее (перечень, пусто) границами не является */
export const toNumericFilterRange = (value: unknown): NumericFilterRange =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as NumericFilterRange) : {};

/** Граница промежутка: число либо число, пришедшее текстом из сохранённых настроек */
const toBound = (bound: unknown): number | undefined => {
  if (typeof bound === 'number') return Number.isFinite(bound) ? bound : undefined;
  if (typeof bound !== 'string' || !bound.trim()) return undefined;
  const parsed = Number(bound);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Приводит сохранённый элемент перечня к разбираемому виду: значение — числом, промежуток —
 * парой границ. Элемент, в котором нет ни числа, ни единой границы, отбором быть не может.
 */
export const toNumericFilterItem = (value: unknown): NumericFilterItem | null => {
  if (typeof value === 'number' || typeof value === 'string') return toBound(value) ?? null;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;

  const { from, to } = value as { from?: unknown; to?: unknown };
  const bounds = { from: toBound(from), to: toBound(to) };
  return bounds.from === undefined && bounds.to === undefined ? null : bounds;
};

/** Набранный перечень: элементы, которые отбором быть не могут, отбрасываются */
export const toNumericFilterItems = (value: unknown): NumericFilterItem[] =>
  isNumericValuesFilter(value)
    ? value.map(toNumericFilterItem).filter((item): item is NumericFilterItem => item !== null)
    : [];

/**
 * Тире, разделяющее границы промежутка. Пробелы вокруг него обязательны: вплотную к границам
 * тире сливается с их знаком минус — `-5-10` не прочитать ни как промежуток, ни как значение.
 */
const RANGE_SEPARATOR = ' – ';

/**
 * Элемент перечня, как он читается пользователем: значение — само собой, промежуток — парой
 * границ. Этим же текстом элемент и набирается: снятый со списка набирается заново как показан.
 */
export const formatNumericFilterItem = (item: unknown): string => {
  const normalized = toNumericFilterItem(item);
  if (normalized === null) return String(item);
  if (typeof normalized === 'number') return String(normalized);

  const from = normalized.from === undefined ? '' : String(normalized.from);
  const to = normalized.to === undefined ? '' : String(normalized.to);
  return `${from}${RANGE_SEPARATOR}${to}`.trim();
};

/** Число может быть значением колонки */
const isValidBound = (value: number, limits: NumericValueLimits): boolean => {
  if (limits.isIntegerOnly && !Number.isInteger(value)) return false;
  return (limits.min === undefined || value >= limits.min) && (limits.max === undefined || value <= limits.max);
};

/**
 * Разбирает один перечисленный элемент: конкретное значение либо промежуток «нижняя – верхняя».
 * Элементом становится только то, что может быть значением колонки; каждая заданная граница
 * промежутка проверяется теми же пределами.
 */
const parseNumericFilterItem = (trimmed: string, limits: NumericValueLimits): NumericFilterItem | null => {
  const range = trimmed.match(RANGE_PATTERN);
  if (range) {
    // Верхняя граница набрана либо вслед за нижней, либо одна — тогда её и держит своя группа
    const [, rawFrom, rawTo, rawToAlone] = range;
    const bounds = { from: toBound(rawFrom), to: toBound(rawTo ?? rawToAlone) };
    const isValid = [bounds.from, bounds.to].every((bound) => bound === undefined || isValidBound(bound, limits));
    return isValid ? bounds : null;
  }

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return null;
  return isValidBound(parsed, limits) ? parsed : null;
};

/**
 * Разбирает строку с перечисленными элементами: разбираемая часть ввода не пропадает —
 * принятые элементы возвращаются вместе, а непринятые остаются в том виде, в каком набраны.
 */
export const parseNumericFilterItems = (raw: string, limits: NumericValueLimits): NumericItemsParseResult => {
  const items: NumericFilterItem[] = [];
  const rejected: string[] = [];

  for (const part of raw.split(ITEMS_SEPARATOR)) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const parsed = parseNumericFilterItem(trimmed, limits);
    if (parsed === null) rejected.push(trimmed);
    else items.push(parsed);
  }

  return { items, rejected };
};

/** Пополняет перечень набранными элементами; повторный элемент перечень не расширяет */
export const appendNumericFilterItems = (
  current: NumericFilterItem[],
  added: NumericFilterItem[],
): NumericFilterItem[] => {
  const keys = new Set(current.map(formatNumericFilterItem));
  const fresh = added.filter((item) => {
    const key = formatNumericFilterItem(item);
    if (keys.has(key)) return false;
    keys.add(key);
    return true;
  });

  return fresh.length ? [...current, ...fresh] : current;
};
